<template>
    <v-container :fluid="true" class="max-height">
        <v-row>
            <v-col class="px-5 d-flex justify-space-between" cols="12">
                <h1 class="text-left">Asset Groups</h1>
                <v-btn color="black" prepend-icon="mdi-plus" @click="createDialogSwitch = true">Add</v-btn>
            </v-col>
        </v-row>
        <h3></h3>
        <v-row>
            <v-col cols="12">
                <v-table>
                    <thead>
                    <tr>
                        <template v-if="user.type === 'admin'">
                            <th>Company</th>
                            <th>Type</th>
                        </template>
                        <th-btn :sortedColumn="sortedColumn" column="name" sortable
                                title="Name"
                                @sort="sort"
                        />
                        <th>Total Assets</th>
                        <th>Non Reporting Count</th>
                        <th>Status</th>
                        <th>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="group in groups" v-if="groups.length" :key="group.id" class="text-left">
                        <template v-if="user.type === 'admin'">
                            <td class="text-capitalize">{{
                                    group.company?.company_name ?? 'N/A'
                                }}
                            </td>
                            <td class="text-capitalize">{{ group.type === 1 ? 'Default' : 'Non Default' }}</td>
                        </template>
                        <td class="text-capitalize">{{ group.name }}</td>
                        <td class="text-capitalize">{{ group.assets_count || 0 }}</td>
                        <td class="text-capitalize">{{ group.non_reporting_devices }}</td>
                        <td class="text-capitalize">{{ group.status }}</td>
                        <td>
                            <v-btn-group v-if="group.type>1" density="compact" divided variant="outlined">
                                <v-btn :disabled="assetsDialogSwitch" prepend-icon="mdi-account-multiple"
                                       size="x-small" @click="groupAssets(group)">Assets
                                </v-btn>
                                <v-btn :disabled="editDialogSwitch" class="text-warning" prepend-icon="mdi-pencil"
                                       size="x-small" @click="editGroup(group)">Edit
                                </v-btn>
                                <v-btn class="text-error" prepend-icon="mdi-delete" size="x-small"
                                       @click="deleteGroup(group)">Delete
                                </v-btn>
                            </v-btn-group>
                        </td>
                    </tr>
                    <tr v-else>
                        <td v-if="loading" colspan="7">
                            <v-progress-circular color="primary" indeterminate/>
                        </td>
                        <td v-else colspan="7">No record found</td>
                    </tr>
                    </tbody>
                </v-table>
            </v-col>
        </v-row>

        <v-row v-if="totalPages > 1" justify="center">
            <v-col cols="8">
                <v-container class="max-width">
                    <v-pagination
                        density="compact"
                        v-model="currentPage"
                        :length="totalPages"
                        class="my-4"
                        @update:model-value="fetchGroups"
                    ></v-pagination>
                </v-container>
            </v-col>
        </v-row>
    </v-container>

    <Create v-if="createDialogSwitch" v-model="createDialogSwitch" @addGroup="addGroup"/>
    <Edit v-if="editDialogSwitch" v-model="editDialogSwitch" :group="selectedGroup" @updateGroup="updateGroup"/>
    <Group-Assets v-if="assetsDialogSwitch" v-model="assetsDialogSwitch" :group="selectedGroup" @newAssets="newAssets"/>
</template>

<script setup>
import {onMounted, ref} from "vue";
import {send} from "@/composables/apiClient";
import {useAuthStore} from "@/stores/authStore";
import ThBtn from "@/components/globals/th-btn.vue";
import {useSnackbarStore} from "@/stores/snackBarStore";
import Edit from "@/components/dialogs/assets/groups/Edit.vue";
import Create from "@/components/dialogs/assets/groups/Create.vue";
import GroupAssets from "@/components/dialogs/assets/groups/GroupAssets.vue";

const groups = ref([]);
const {user} = useAuthStore();
const loading = ref(true);
const totalPages = ref(1);
const currentPage = ref(1);
const sortedType = ref(null);
const sortedColumn = ref(null);
const selectedGroup = ref(null);
const {showSnackbar} = useSnackbarStore();
const editDialogSwitch = ref(false);
const createDialogSwitch = ref(false);
const assetsDialogSwitch = ref(false);

onMounted(() => {
    fetchGroups();
});

const sort = (sorting) => {
    sortedColumn.value = sorting.column;
    sortedType.value = sorting.order;
    fetchGroups();
}
const fetchGroups = () => {
    send('get', `asset-groups`, {
        page: currentPage.value,
        order: sortedType.value,
        sort: sortedColumn.value,
    }).then((response) => {
        groups.value = response.data.data.data;
        totalPages.value = response.data.data.last_page;
        currentPage.value = response.data.data.current_page;
    }).catch((error) => {
        console.log(error)
        showSnackbar('Groups could not be loaded', 'error')
    }).finally(() => loading.value = false)
}
const addGroup = (group) => {
    groups.value.unshift(group);
}
const editGroup = (group) => {
    selectedGroup.value = group;
    editDialogSwitch.value = true;
}
const updateGroup = (group) => {
    groups.value.map((comp, index) => {
        if (comp.id === group.id) {
            groups.value[index] = group;
        }
    });
}
const deleteGroup = (group) => {
    if (confirm(`Are you sure to delete "${group.name}" asset group?`)) {
        send('delete', `asset-groups/${group.id}`, null)
            .then((response) => {
                if (response.data.success) {
                    groups.value.map((item, index) => {
                        if (item.id === group.id) {
                            groups.value.splice(index, 1);
                        }
                    });
                    showSnackbar(response.data.message);
                }
            })
            .catch((error) => {
                if (error.response.data.message) {
                    showSnackbar(error.response.data.message, 'error')
                } else {
                    showSnackbar(`"${group.name}" Asset group could not be deleted`, 'error')
                }
            })
    }
}
const groupAssets = (group) => {
    selectedGroup.value = group;
    assetsDialogSwitch.value = true;
}
const newAssets = () => {
    fetchGroups();
}
</script>
